// Angular
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Libraries
import { ActivatedRoute, Router } from '@angular/router';
// Rxjs
import { distinctUntilChanged, map, skip, switchMap, tap } from 'rxjs/operators';
// Afidium
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { IDateFieldSpecs, IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { EnumsResourceService } from 'libraries/service/booking/enums/enums-resource.service';
import { ISelectOption } from 'libraries/module/formbuilder/fieldsets/generic-fieldset.component';
import { IGetEnumsAgency } from 'libraries/service/booking/enums/IGetEnums';
import { HelperService } from 'app_sasha/shared/services/helper.service';
import { ITicketingReportSearchParameters, IBookingInTicketingReportSearchParametersUrl, TicketingReportSearchParameters } from './BookingInTicketingReportSearchParameters';
import { IMultiselectOptions } from 'libraries/module/formbuilder/fields/multiselect/multiselect.model';
import { ConfigurationResourceService } from 'libraries/service/resources/configuration/configurationResource.service';
import { ResourceError } from 'libraries/service/resource/ResourceError';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { SashaBreadcrumbService } from 'app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service';
import { SearchUtils } from 'app_sasha/shared/models/SearchUtils';
import { DateService } from "libraries/date/date.service";
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { HostAliasResource, IHostCodesAndAliases } from "libraries/service/resources/host-alias/host-alias.resource";
import { BookingInTicketingReportRouting } from "./booking-in-ticketing-report.routing";

interface IFieldConfig {
    system: {
        config: IFieldSpecs;
        items: ISelectOption[];
    };
    agency: {
        config: IFieldSpecs;
        items: ISelectOption[];
    };
    max: {
        config: IDateFieldSpecs;
    };
}

@Component({
    selector: 'booking-in-ticketing-report',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>

            <card>
                <div class="card-header-content">
                    <h1>{{ labels.title }}</h1>
                </div>
                <div class="card-body-content card-marge-inside">
                    <notification *ngIf="notification.active" [type]="notification.type">{{ notification.message }}</notification>

                    <form #form [formGroup]="ticketingReportForm">
                        <date-field [fieldConfig]="fieldConfig.max.config"></date-field>

                        <combobox-field
                            [fieldConfig]="fieldConfig.system.config"
                            [items]="fieldConfig.system.items"></combobox-field>

                        <combobox-field
                            [fieldConfig]="fieldConfig.agency.config"
                            [items]="fieldConfig.agency.items"></combobox-field>

                        <btn [label]="labels.btn"
                            btnType="submit"
                            styleType="actionPrincipal"
                            [disableIf]="!ticketingReportForm.get('system').value || !ticketingReportForm.get('agency').value"
                            (click)="onSubmit()"></btn>
                    </form>
                </div>
            </card>
        </div>
    `,
    styles: [`
        combobox-field, date-field {
            display: block;
            width: 25%;
            margin-bottom: .75rem;
        }

        notification {
            margin-bottom: .75rem;
        }
    `]
})
export class BookingInTicketingReportComponent implements OnInit {
    public readonly labels: ILabels = {
        title: Yii.t('wui-sasha-js', "Lister les dossiers dans l'état de caisse"),
        btn: Yii.t('wui-sasha-js', "Lister les dossiers")
    };

    public fieldConfig: IFieldConfig;

    public ticketingReportForm: FormGroup = this.formBuilder.group({
        system: [null, Validators.required],
        agency: [null, Validators.required],
        max: [new Date()]
    });

    public notification: INotification = {
        active: false,
        type: NotificationType.Error,
        message: Yii.t('wui-sasha-js', 'Une erreur est survenue')
    };

    public searchParameters: TicketingReportSearchParameters = new TicketingReportSearchParameters();

    private readonly processLabels: ILabels = {
        noSystemSelected: Yii.t('wui-sasha-js', 'Sélectionner un système'),
        noAgencySelected: Yii.t('wui-sasha-js', 'Sélectionner une agence'),
        noAgency: Yii.t('wui-sasha-js', 'Aucune agence disponible pour ce système'),
        loadAgency: Yii.t('wui-sasha-js', 'Chargement des agences en cours...')
    };

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly enumsResourceService: EnumsResourceService,
        private readonly helperService: HelperService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly dateService: DateService,
        private readonly titleService: TitleService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly hostAliasResource: HostAliasResource,
    ) { }

    public ngOnInit(): void {
        this.fieldConfig = initFieldConfig(this.ticketingReportForm);

        this.titleService.setTitle(Yii.t("wui-sasha-js", "État de caisse"));

        this.configurationResourceService.get<string[]>(['wui', 'app_sasha', 'config', 'ticket-page-system', 'system.json']).pipe(
            switchMap((hostCodes: string[]) => this.hostAliasResource.getCodesAndAliases(hostCodes)),
            map((hostCodesAndAliases: IHostCodesAndAliases) =>
                this.helperService.getMultiselectOptions(hostCodesAndAliases.aliases, hostCodesAndAliases.codes, true))
        ).subscribe({
            next: (systems: ISelectOption[]): void => {
                this.fieldConfig.system.items = systems;
            },
            error: (error: ResourceError): void => {
                this.notification = {
                    active: true,
                    type: NotificationType.Error,
                    message: Yii.t('wui-sasha-js', "Le fichier system.json n'existe pas")
                };
            }
        });

        this.fieldConfig.system.config.placeholder = this.processLabels.noSystemSelected;
        const paramsURL: IBookingInTicketingReportSearchParametersUrl = this.activatedRoute.snapshot.queryParams;
        this.sashaBreadcrumbService.setBookingInTicketingReportSearch(paramsURL);

        let params: ITicketingReportSearchParameters = TicketingReportSearchParameters.fromRouteParams(paramsURL);

        this.ticketingReportForm.get('system').valueChanges.pipe(
            distinctUntilChanged(),
            tap(() => this.fieldConfig.agency.config.placeholder = this.processLabels.loadAgency),
            switchMap((system: string) => this.enumsResourceService.listAgencies()),
            map(getSpecificMultiselectOptions),
            tap((agencies: ISelectOption[]) => {
                if (!this.ticketingReportForm.get('system').value) {
                    this.fieldConfig.agency.config.placeholder = this.processLabels.noSystemSelected;
                } else if (agencies.length > 0) {
                    this.fieldConfig.agency.config.placeholder = this.processLabels.noAgencySelected;
                    this.fieldConfig.agency.items = agencies;
                } else if (agencies.length === 0) {
                    this.fieldConfig.agency.config.placeholder = this.processLabels.noAgency;
                    this.fieldConfig.agency.items = [];
                }
            }),
            skip(1),
            tap((agencies: ISelectOption[]) => {
                this.ticketingReportForm.get('agency').patchValue(agencies.length === 1 ? agencies[0].value : null);
            })
        ).subscribe();

        params = {
            ...params,
            max: this.getDateFromParams(params)
        };

        this.ticketingReportForm.patchValue(params);
    }

    public onSubmit(): void {
        const { system, agency }: ITicketingReportSearchParameters = this.ticketingReportForm.value;

        if (system && agency) {
            this.searchParameters = SearchUtils.populateSearchParametersFromForm<TicketingReportSearchParameters>(
                this.ticketingReportForm,
                this.searchParameters
            );

            const params: IBookingInTicketingReportSearchParametersUrl = this.searchParameters.convertToUrlParameters();
            this.router.navigate(
                ['sasha', 'ticketing-report'], { queryParams: params, replaceUrl: true }
            ).then(// then change route to reach '/search-result/'
                async () => this.router.navigate(
                    ['sasha', 'booking-in-ticketing-report', 'search-result'], { queryParams: params }
                )
            ).catch((error: Error) => { this.errorTracingService.captureException(error); });
        }
    }

    private getDateFromParams(params: ITicketingReportSearchParameters): Date {
        if (!params.max) {
            return new Date();
        }
        return params.max instanceof Date ? params.max : this.dateService.getStr2JsDate(params.max);
    }
}

function initFieldConfig(ticketingReportForm: FormGroup): IFieldConfig {
    return {
        system: {
            items: [],
            config: {
                formGroup: ticketingReportForm,
                formControlName: 'system',
                label: Yii.t('wui-sasha-js', 'Système'),
            }
        },
        agency: {
            items: [],
            config: {
                formGroup: ticketingReportForm,
                formControlName: 'agency',
                label: Yii.t('wui-sasha-js', 'Agence'),
            }
        },
        max: {
            config: {
                formGroup: ticketingReportForm,
                formControlName: 'max',
                label: Yii.t('wui-sasha-js', 'A la date du'),
                maxDate: new Date(),
                icon: 'fa fa-calendar',
                placeholder: Yii.t('wui-sasha-js', 'Sélectionner')
            }
        }
    };
}

function getSpecificMultiselectOptions(values: IGetEnumsAgency[]): IMultiselectOptions[] {
    return values.map(({ name, code }: IGetEnumsAgency) => ({ label: `${name} (${code})`, value: code }));
}
